// ── The one module of the browser storage (#457, #664 F77) ────────────────────
// A browser can block the site data: private mode, a policy, a full storage.
// Then each call of `localStorage` or `sessionStorage` throws, and a bare
// read at module load gave a blank page (#457). A damaged value throws in
// JSON.parse in the same way. This file holds the one guard for both cases.
// Each call returns its fallback when the storage throws.
//
// This file has NO imports. `store/auth.ts` and `i18n/index.ts` read it at
// module load, and `observability.ts` reads it with no cycle.
//
// Only this file touches the storage: `safe-storage-ratchet.spec.ts`
// fails on a bare call anywhere else in `frontend/src`.
// ─────────────────────────────────────────────────────────────────────────────

export interface SafeStorage {
  /** The stored text. Return `null` when the key is not set or the storage throws. */
  get(key: string): string | null;
  /** Store the text. Return `false` when the storage throws. */
  set(key: string, value: string): boolean;
  /** Remove the key. Do nothing when the storage throws. */
  remove(key: string): void;
  /** The keys that start with the prefix, as a copy. A caller can remove them in a loop. Return `[]` when the storage throws. */
  keysWithPrefix(prefix: string): string[];
  /** The stored JSON value. Return `fallback` when the key is not set, the text is not JSON, or the storage throws. */
  getJson<T>(key: string, fallback: T): T;
  /** Store the value as JSON. A value with no JSON form (`undefined`, a function) removes the key. The next read then gives the fallback, and the call returns `true`. Return `false` when the storage throws or `JSON.stringify` throws (a cycle). */
  setJson(key: string, value: unknown): boolean;
}

/** `resolve` runs at each call. The old code read `localStorage` at each call, and a getter that throws (blocked site data) must not stop the page. */
function make(resolve: () => Storage | null): SafeStorage {
  const attempt = <T>(fallback: T, run: (storage: Storage) => T): T => {
    try {
      const storage = resolve();
      return storage ? run(storage) : fallback;
    } catch {
      return fallback;
    }
  };
  return {
    get: key => attempt<string | null>(null, s => s.getItem(key)),
    set: (key, value) => attempt(false, s => { s.setItem(key, value); return true; }),
    remove: key => attempt<void>(undefined, s => s.removeItem(key)),
    keysWithPrefix: prefix => attempt<string[]>([], s => {
      const keys: string[] = [];
      for (let i = 0; i < s.length; i++) {
        const key = s.key(i);
        if (key !== null && key.startsWith(prefix)) keys.push(key);
      }
      return keys;
    }),
    getJson: <T>(key: string, fallback: T) => attempt<T>(fallback, s => {
      const raw = s.getItem(key);
      return raw === null ? fallback : (JSON.parse(raw) as T);
    }),
    setJson: (key, value) => attempt(false, s => {
      const text = JSON.stringify(value);
      if (text === undefined) s.removeItem(key);
      else s.setItem(key, text);
      return true;
    }),
  };
}

/** The guard for a storage that the caller holds. `null` means no storage: every call returns its fallback. */
export function guard(storage: Storage | null): SafeStorage {
  return make(() => storage);
}

/** The storage of the site, for the choices that last: the language, the user blob, a snooze. */
export const local: SafeStorage = make(() => window.localStorage);

/** The storage of the tab, for a value of one visit: a scroll position, a timer, a crash count. */
export const session: SafeStorage = make(() => window.sessionStorage);
